import { useCallback, useEffect, useState } from 'react'

import { CONTRACT_SPACES_REGISTRY_ADDRESS } from '@/constants/blockchain'
import L1SpacesRegistry from '@/contracts/SpacesRegistry'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

import CreationsCard from '../components/Studio/CreationsCard'
import { SpaceInfo, Template, verifyTemplateSchema } from '../components/Studio/Templates/utils'
import { ErrorMessageDisplay, fetchIpfsMetadata } from '../utils/utils'
import { useChainFunctions } from './useChain'
import useSessionStorage from './useSessionStorage'

const useSpaceRegistry = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ErrorMessageDisplay>()
  const [spacesMetadata, setSpacesMetadata] = useSessionStorage<{ [id: string]: SpaceInfo }>(
    'spacesMetadata',
    {}
  )

  const { readContract } = useChainFunctions(l1NativeWagmiConfig)

  const getTemplatesBySpaces = useCallback(
    (spacesId: string, spaces: SpaceInfo) =>
      spaces?.metadataInfo?.templates
        ? Object.entries(spaces?.metadataInfo?.templates).map(([key, value]) => {
            // Verify template schema
            if (!verifyTemplateSchema(value as Template)) return <></>
            // Show template on Carousel
            const { name, shortDescription, image } = value as Template
            return (
              <CreationsCard
                key={key}
                templateId={`${spacesId}-${key}`}
                title={name}
                description={shortDescription}
                image={image}
              />
            )
          })
        : null,
    []
  )

  const getSpacesMetadata = useCallback(async (id: number) => {
    let info: SpaceInfo
    try {
      const data = await readContract({
        address: CONTRACT_SPACES_REGISTRY_ADDRESS as `0x${string}`,
        abi: L1SpacesRegistry,
        functionName: 'getSpaceInfo',
        args: [BigInt(id)],
      })
      info = data as SpaceInfo
    } catch (err: any) {
      setError({
        message: 'Error fetching spaces id from contract',
        details: `Space with id ${id} not found. ${err?.message}`,
      })
      return
    }

    let metadata: any
    try {
      metadata = await fetchIpfsMetadata(info.metadata)
    } catch (err: any) {
      setError({
        message: 'Error fetching spaces metadata from IPFS',
        details: `${err.message}`,
      })
      return
    }
    const newSpace: SpaceInfo = {
      name: info.name,
      url: info.url,
      metadata: info.metadata,
      metadataInfo: metadata,
      active: info.active,
    }
    setSpacesMetadata(prevState => ({
      ...prevState,
      [`${id}`]: newSpace,
    }))
  }, [])

  useEffect(() => {
    async function getTemplates() {
      let total: number
      try {
        const data = await readContract({
          address: CONTRACT_SPACES_REGISTRY_ADDRESS as `0x${string}`,
          abi: L1SpacesRegistry,
          functionName: 'totalSpaces',
        })
        total = Number(data)
      } catch (e: any) {
        setError({
          message: 'Error fetching total number of spaces',
          details: `${e.message}`,
        })
        return
      }
      // Useful to avoid fetching all spaces metadata on every render if there's not a new one but cannot be used if we need to fetch metadata specific updates
      if (Object.keys(spacesMetadata).length === total) {
        setLoading(false)
        return
      }
      setLoading(true)
      const promises = Array.from({ length: total }, (_, i) => getSpacesMetadata(i + 1))
      await Promise.all(promises)
      setLoading(false)
    }
    // Fetching on every render is not ideal, but we need to fetch metadata updates
    try {
      getTemplates()
    } catch (e: any) {
      console.error(e)
      setError(e.message)
    }
  }, [spacesMetadata])

  // Return the necessary state and functions
  return {
    loading,
    error,
    spacesMetadata,
    getSpacesMetadata,
    getTemplatesBySpaces,
  }
}

export default useSpaceRegistry
